import TableChartOutlined from "@mui/icons-material/TableChartOutlined";
import { type FC, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { SystemWizardDialog, type WizardStep } from "../../../../../components/UIToolkit/Popups/SystemWizardDialog";
import { useHospitalData } from "../../../../../contexts/HospitalDataProvider";
import type { GeneralFormData, SubmitDeptConfigResult } from "../types";
import { AirtableRecordStep } from "./AirtableRecordStep";
import { AirtableReviewStep } from "./AirtableReviewStep";
import { ambiguousFacilityNames, filterAirtableFacilities, mergeAirtableFacilities } from "./airtableFacilityFilter";
import { useAirtableFacilities, useAirtableFacilitySearch } from "./airtableImportHooks";
import { airtableErrorMessage } from "./airtableImportLabels";
import type { AirtableFacilityOption } from "./airtableImportTypes";
import { useAirtableImportFlow } from "./useAirtableImportFlow";
import { useAirtableReview } from "./useAirtableReview";

type StepId = "record" | "review";

type Props = {
	open: boolean;
	onClose: () => void;
	/** Live form state — the merge base, and the source of truth for which fields exist. */
	formData: GeneralFormData;
	/** The form has unsaved edits; the review step warns about it rather than blocking. */
	isDirty: boolean;
	/**
	 * Hand the merged form back to the page, resolving with whether the save actually went through.
	 * The caller must NOT submit in the same tick: see `DepartmentConfiguration`'s deferred-apply
	 * effect for why.
	 */
	onApply: (merged: GeneralFormData) => Promise<SubmitDeptConfigResult>;
	/** Already-translated reasons the department config currently refuses to save. */
	saveBlockedReasons: string[];
};

/**
 * The support-only path from an implementation-intake record to a department's saved config: pick the
 * record, then accept its proposed field changes one by one. The department it lands on is the one open
 * in Settings, named in the dialog's subtitle.
 *
 * Nothing here writes. The accepted rows are merged into the existing form and saved through the
 * department config's own Save path — which is what keeps the timezone-409 and job-removal conflict
 * dialogs working with no new code, and what keeps the four "omitted key = leave untouched" config
 * fields (`autoApproveRules`, `censusIntervals`, `jobShiftTimes`, `staffScheduleDisplayUntilDate`)
 * intact. A hand-built payload would wipe them.
 */
export const AirtableImportWizard: FC<Props> = ({ open, onClose, formData, isDirty, onApply, saveBlockedReasons }) => {
	const { t } = useTranslation();
	const { chosenDepartment } = useHospitalData();

	const [stepId, setStepId] = useState<StepId>("record");
	const [query, setQuery] = useState("");
	const [selectedFacility, setSelectedFacility] = useState<AirtableFacilityOption | null>(null);

	// One fetch for the whole base when the step opens; every keystroke after that is a memory filter.
	const { data: facilities, isLoading: isLoadingFacilities, error: searchError } = useAirtableFacilities({ enabled: open && stepId === "record" });
	// Only a TRUNCATED listing needs Airtable's own search: a record past its end can't be filtered into view.
	const baseSearch = useAirtableFacilitySearch({ query, enabled: open && stepId === "record" && facilities?.truncated === true });
	const candidates = useMemo(
		// DEFAULT: no search has run (the listing was whole, or the query is too short), so there are no extra hits.
		() => (facilities ? mergeAirtableFacilities(facilities.candidates, baseSearch.data?.candidates ?? []) : undefined),
		[facilities, baseSearch.data],
	);
	const matches = useMemo(() => filterAirtableFacilities(candidates ?? [], query), [candidates, query]);
	const sharedFacilityNames = useMemo(() => ambiguousFacilityNames(candidates ?? []), [candidates]);
	const baseSearchErrorMessage = baseSearch.error ? t(airtableErrorMessage(baseSearch.error).key, airtableErrorMessage(baseSearch.error).params) : null;

	const review = useAirtableReview();
	const { acceptedRows, applyDisabledReasons, setChecked } = review;

	const onPreviewLoaded = useCallback(() => setStepId("review"), []);
	const flow = useAirtableImportFlow({ review, formData, onApply, onClose, onPreviewLoaded });

	const searchErrorMessage = searchError ? t(airtableErrorMessage(searchError).key, airtableErrorMessage(searchError).params) : null;
	const isBusy = flow.isVerifying || flow.isPreviewing || flow.isSaving;

	const steps: WizardStep<StepId>[] = [
		{
			id: "record",
			label: t("department.config.airtable.step.record"),
			content: (
				<AirtableRecordStep
					query={query}
					onQueryChange={setQuery}
					matches={matches}
					// Absent data is either the in-flight fetch or the failure beside it, and the step renders
					// one of those two states instead of a list — so an empty/whole list here is never shown.
					totalCount={candidates?.length ?? 0}
					isLoading={isLoadingFacilities}
					errorMessage={searchErrorMessage}
					truncated={facilities?.truncated ?? false}
					isSearchingBase={baseSearch.isFetching}
					baseSearchErrorMessage={baseSearchErrorMessage}
					selectedFacility={selectedFacility}
					previewErrorMessage={flow.stepError}
					ambiguousFacilityNames={sharedFacilityNames}
					onSelect={(recordId) => {
						const facility = candidates?.find((option) => option.recordId === recordId);
						if (facility) setSelectedFacility(facility);
					}}
				/>
			),
			primaryLabel: t("continue"),
			// Continue builds the diff; the step only moves on once it is in hand, so the rail can never
			// show "Review" for a diff that failed to load — the failure renders on this step instead.
			onPrimary: () => selectedFacility && flow.runPreview(selectedFacility.recordId),
			primaryDisabled: !selectedFacility || flow.isPreviewing,
			primaryLoading: flow.isPreviewing,
			primaryTooltip: selectedFacility ? undefined : t("department.config.airtable.search.pickFirst"),
		},
		{
			id: "review",
			label: t("department.config.airtable.step.review"),
			content: (
				<AirtableReviewStep
					review={review}
					isDirty={isDirty}
					staleRecordMessage={flow.staleRecordMessage}
					errorMessage={flow.stepError}
					duplicateRecordIds={flow.duplicateRecordIds}
					duplicatesTruncated={flow.duplicatesTruncated}
					// Named on the refusal banner: "the save was refused" without saying which fields is a
					// dead end, and these reasons are computed off the MERGED form so they are the real ones.
					saveBlockedReasons={flow.stepError ? saveBlockedReasons : []}
					onCheckedChange={setChecked}
					disabled={isBusy}
				/>
			),
			primaryLabel: t("department.config.airtable.apply.action", { count: acceptedRows.length }),
			onPrimary: () => selectedFacility && flow.runApply(selectedFacility),
			primaryDisabled: applyDisabledReasons.length > 0 || isBusy,
			primaryLoading: flow.isVerifying || flow.isSaving,
			// Same contract as the Save button's `saveDisabledReasons`: a dead primary always says why.
			primaryTooltip: applyDisabledReasons.length > 0 ? applyDisabledReasons.map((reason) => t(reason)).join(" · ") : undefined,
		},
	];

	// Nothing here has committed a write, so walking away is always safe — but not mid-verify, where the
	// answer decides whether the operator's decisions are still valid.
	const handleClose = useCallback(() => {
		if (flow.isVerifying || flow.isSaving) return;
		onClose();
	}, [flow.isVerifying, flow.isSaving, onClose]);

	return (
		<SystemWizardDialog<StepId>
			open={open}
			onClose={handleClose}
			icon={<TableChartOutlined />}
			title={t("department.config.airtable.title")}
			subtitle={t("department.config.airtable.subtitle", { department: chosenDepartment.display_name })}
			steps={steps}
			activeStepId={stepId}
			onStepChange={(next) => {
				flow.setStepError(null);
				setStepId(next);
			}}
			backLabel={t("back")}
			closeLabel={t("close")}
			railAriaLabel={t("department.config.airtable.rail.ariaLabel")}
			maxWidth={stepId === "review" ? "xl" : "md"}
			dataTestId="airtable-import-wizard"
		/>
	);
};
